/**
 * In-app drawer for the legal-documents viewer (CWS-21215), gated behind the
 * cup-legal-documents-in-app-drawer flag (WithLegalDocumentsDrawer). Every
 * [data-legal-documents-drawer-trigger] link already points at the standalone
 * viewer's own route (legal_documents_viewer_controller.rb) — clicking one
 * fetches that same route as a fragment (layout: false, triggered by the
 * X-Requested-With header below) and re-runs initLegalDocumentsViewer against
 * the injected subtree, with enableHistory off so the drawer never rewrites
 * this page's own address bar. With JS disabled, or if the fetch fails, the
 * link is a real href and still navigates to the full standalone page.
 */
import { initLegalDocumentsViewer } from './legal_documents_viewer';
import { initLanguageSelector } from './language_selector';

const FOCUSABLE_SELECTOR = 'button, [href], select, input, [tabindex]:not([tabindex="-1"])';

export const initLegalDocumentsDrawer = () => {
  const drawer = document.getElementById('legal-documents-drawer');
  if (!drawer) return;

  const panel = drawer.querySelector('.cropwise-accounts-legal-drawer__panel');
  const closeButton = document.getElementById('legal-documents-drawer-close');
  const scrim = drawer.querySelector('[data-legal-documents-drawer-dismiss]');
  const slot = document.getElementById('legal-documents-drawer-slot');
  const loadingMarkup = slot.innerHTML;

  let lastTrigger = null;
  let requestToken = 0;

  // data-state tracks the slide animation itself (not just hidden, which
  // flips at the *start* of the transition) so callers — including system
  // specs — can wait for the drawer to have actually finished moving before
  // interacting with anything inside it.
  const openDrawer = () => {
    drawer.hidden = false;
    drawer.dataset.state = 'opening';
    requestAnimationFrame(() => requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      panel.addEventListener('transitionend', () => {
        drawer.dataset.state = 'open';
      }, { once: true });
    }));
  };

  const closeDrawer = () => {
    // Invalidates any in-flight fragment request so it cannot finish after
    // the close, initializing the now-detached/hidden content and stealing
    // focus back from lastTrigger via load's own closeButton.focus() call.
    requestToken += 1;
    drawer.classList.remove('is-open');
    drawer.dataset.state = 'closing';
    panel.addEventListener('transitionend', () => {
      drawer.hidden = true;
      drawer.dataset.state = 'closed';
      slot.innerHTML = loadingMarkup;
      lastTrigger?.focus();
    }, { once: true });
  };

  const showError = () => {
    slot.replaceChildren();
    const message = document.createElement('p');
    message.className = 'cropwise-accounts-legal-drawer__error';
    message.setAttribute('role', 'alert');
    message.textContent = slot.dataset.errorText;
    slot.append(message);
  };

  const load = async (href) => {
    requestToken += 1;
    const token = requestToken;
    slot.innerHTML = loadingMarkup;
    try {
      const response = await fetch(href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (token !== requestToken) return;
      if (!response.ok) throw new Error(`Failed to load legal documents: ${response.status}`);
      const html = await response.text();
      if (token !== requestToken) return;
      slot.innerHTML = html;
      const injectedRoot = slot.querySelector('#legal-documents-viewer');
      // Changing location inside the drawer re-fetches and re-renders the
      // new country's document through this same load, instead of load's
      // caller (open) real-navigating away like the standalone page does.
      // initialUrl seeds the viewer's own URL tracking with the href just
      // fetched, so a country with no current document (no button opens
      // to set this itself) can still be navigated away from and back to.
      initLegalDocumentsViewer(injectedRoot, {
        enableHistory: false,
        onLocationChange: load,
        initialUrl: href,
      });
      initLanguageSelector(injectedRoot, '#legal-documents-viewer-language-selector');
      closeButton.focus();
    } catch {
      if (token === requestToken) showError();
    }
  };

  const open = (trigger) => {
    lastTrigger = trigger;
    openDrawer();
    void load(trigger.href);
  };

  const trapFocus = (event) => {
    if (event.key !== 'Tab') return;
    const focusable = [...panel.querySelectorAll(FOCUSABLE_SELECTOR)];
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-legal-documents-drawer-trigger]');
    if (!trigger) return;

    event.preventDefault();
    open(trigger);
  });

  closeButton.addEventListener('click', closeDrawer);
  scrim.addEventListener('click', closeDrawer);
  panel.addEventListener('keydown', trapFocus);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !drawer.hidden) closeDrawer();
  });
  addEventListener('popstate', () => {
    if (!drawer.hidden) closeDrawer();
  });
};
